<div class="dashboard-page">
  <div class="dashboard-page__inner">
    <div class="welcome">
      <div class="welcome__text">
        <h1>Welcome back@if (session) {, <span class="welcome__name">{{ firstName() }}</span>}</h1>
        <p>Here's a summary of your account on the Cross Border Data Exchange network.</p>
      </div>
      <span class="welcome__date">{{ today | date: 'EEEE, MMM d, y' }}</span>
    </div>

    @if (stats().length > 0) {
      <section class="panel">
        <div class="panel__header">
          <h2 class="panel__title">Overview</h2>
        </div>
        <div class="panel__body">
          <div class="stats-grid">
            @for (stat of stats(); track stat.label) {
              <a [routerLink]="stat.link" class="stat-tile">
                <div class="stat-tile__text">
                  <span class="stat-tile__label">{{ stat.label }}</span>
                  <span class="stat-tile__value">{{ stat.value }}</span>
                </div>
                <div class="stat-tile__icon stat-tile__icon--{{ stat.accent }}"><span class="material-icons">{{ stat.icon }}</span></div>
              </a>
            }
          </div>
        </div>
      </section>
    } @else if (session && !hasStatsSources) {
      <section class="panel">
        <div class="panel__body">
          <div class="empty-hint">
            <span class="material-icons">insights</span>
            <p>Your account doesn't have any live stats to show here yet — use the menu on the left to get started.</p>
          </div>
        </div>
      </section>
    }

    @if (canViewTrendChart) {
      <div class="dashboard-row dashboard-row--two-one">
        <section class="panel dashboard-row__main">
          <div class="panel__header panel__header--split">
            <h2 class="panel__title">Export request volume</h2>
            <div class="chart-period">
              <button [class.chart-period__btn--active]="chartPeriod() === 'week'" class="chart-period__btn" (click)="setChartPeriod('week')">Week</button>
              <button [class.chart-period__btn--active]="chartPeriod() === 'month'" class="chart-period__btn" (click)="setChartPeriod('month')">Month</button>
              <button [class.chart-period__btn--active]="chartPeriod() === 'year'" class="chart-period__btn" (click)="setChartPeriod('year')">Year</button>
            </div>
          </div>
          <div class="panel__body">
            @if (isLoadingChart() && !chartRender()) {
              <p class="hint">Loading...</p>
            } @else if (chartRender()) {
              <div class="chart-summary">
                <div class="chart-summary__item">
                  <span class="chart-summary__label">Total requests</span>
                  <span class="chart-summary__value">{{ chartTotal() }}</span>
                </div>
                <div class="chart-summary__item">
                  <span class="chart-summary__label">Accepted</span>
                  <span class="chart-summary__value">
                    {{ chartAccepted() }}
                    @if (chartTotal() > 0) {
                      <span class="chart-summary__suffix">{{ chartAcceptedRate }}%</span>
                    }
                  </span>
                </div>
                <div class="chart-summary__item">
                  <span class="chart-summary__label">Period</span>
                  <span class="chart-summary__value">{{ chartPeriodLabel }}</span>
                </div>
                <div class="chart-summary__item">
                  <span class="chart-summary__label">Busiest</span>
                  <span class="chart-summary__value">{{ chartPeakLabel() }}</span>
                </div>
              </div>

              <div class="trend-chart" [class.trend-chart--loading]="isLoadingChart()">
                <div class="trend-chart__plot">
                  @for (tick of chartRender()!.ticks; track tick.value) {
                    <div class="trend-chart__gridline" [style.bottom.%]="tick.pct">
                      <span class="trend-chart__tick">{{ tick.value }}</span>
                    </div>
                  }

                  <div class="trend-chart__bars">
                    @for (bar of chartRender()!.bars; track bar.label) {
                      <div class="trend-chart__slot">
                        <div class="trend-chart__bar" [style.height.%]="bar.heightPct"></div>
                        <span
                          class="trend-chart__dot"
                          [style.bottom.%]="bar.acceptedPct"
                        ></span>
                        <div
                          class="chart-tooltip"
                          [style.bottom]="'min(calc(' + bar.heightPct + '% + 14px), calc(100% - 82px))'"
                        >
                          <span class="chart-tooltip__title">{{ bar.label }}</span>
                          <span class="chart-tooltip__row">
                            <span class="chart-tooltip__key chart-tooltip__key--bar"></span>
                            <span class="chart-tooltip__value">{{ bar.count }}</span> created
                          </span>
                          <span class="chart-tooltip__row">
                            <span class="chart-tooltip__key chart-tooltip__key--line"></span>
                            <span class="chart-tooltip__value">{{ bar.accepted }}</span> accepted
                          </span>
                          @if (bar.count > 0) {
                            <span class="chart-tooltip__rate">{{ bar.acceptedRate }}% acceptance</span>
                          }
                        </div>
                      </div>
                    }
                  </div>

                  <svg class="trend-chart__overlay" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                    <path
                      class="trend-chart__line"
                      [attr.d]="chartRender()!.acceptedPath"
                      fill="none"
                      vector-effect="non-scaling-stroke"
                    />
                  </svg>
                </div>

                <div class="trend-chart__labels">
                  @for (bar of chartRender()!.bars; track bar.label) {
                    <span class="trend-chart__label">{{ bar.label }}</span>
                  }
                </div>
              </div>

              <div class="chart-legend">
                <span class="chart-legend__item">
                  <span class="chart-legend__swatch"></span>
                  Requests created
                </span>
                <span class="chart-legend__item">
                  <span class="chart-legend__line"></span>
                  Accepted
                </span>
              </div>
            }
          </div>
        </section>

        <section class="panel dashboard-row__side">
          <div class="panel__header">
            <h2 class="panel__title">Export requests by status</h2>
          </div>
          <div class="panel__body">
            @if (isLoadingBreakdown()) {
              <p class="hint">Loading...</p>
            } @else {
              <div class="donut">
                <svg class="donut__svg" viewBox="0 0 140 140" role="img" aria-label="Export requests by status">
                  <circle class="donut__track" cx="70" cy="70" [attr.r]="donutRadius" />
                  @for (arc of statusArcs(); track arc.key) {
                    @if (arc.count > 0) {
                      <circle
                        class="donut__arc"
                        cx="70"
                        cy="70"
                        [attr.r]="donutRadius"
                        [attr.stroke]="arc.color"
                        [attr.stroke-dasharray]="arc.dash"
                        [attr.stroke-dashoffset]="arc.offset"
                      >
                        <title>{{ arc.label }}: {{ arc.count }}</title>
                      </circle>
                    }
                  }
                </svg>
                <div class="donut__center">
                  <span class="donut__value">{{ statusBreakdownTotal() }}</span>
                  <span class="donut__label">requests</span>
                </div>
              </div>

              <ul class="status-legend">
                @for (arc of statusArcs(); track arc.key) {
                  <li class="status-legend__item">
                    <span class="status-legend__dot" [style.background]="arc.color"></span>
                    <span class="status-legend__label">{{ arc.label }}</span>
                    <span class="status-legend__count">{{ arc.count }}</span>
                    <span class="status-legend__pct">{{ arc.pct | number: '1.0-0' }}%</span>
                  </li>
                }
              </ul>
            }
          </div>
        </section>
      </div>
    }

    @if (canReviewCompanies) {
      <section class="panel">
        <div class="panel__header panel__header--split">
          <h2 class="panel__title">Company review</h2>
          <div class="panel__header-tools">
            <div class="chart-period">
              @for (filter of companyFilters; track filter.value) {
                <button
                  class="chart-period__btn"
                  [class.chart-period__btn--active]="companyFilter() === filter.value"
                  (click)="setCompanyFilter(filter.value)"
                >
                  {{ filter.label }}
                </button>
              }
            </div>
            <a routerLink="/admin/companies" class="panel__link">View all</a>
          </div>
        </div>

        <div class="split-panel">
          <div class="split-panel__aside">
            @if (isLoadingCompanyCounts()) {
              <p class="hint">Loading...</p>
            } @else {
              <div class="donut donut--compact">
                <svg class="donut__svg" viewBox="0 0 140 140" role="img" aria-label="Companies by status">
                  <circle class="donut__track" cx="70" cy="70" [attr.r]="donutRadius" />
                  @for (arc of companyArcs(); track arc.key) {
                    @if (arc.count > 0) {
                      <circle
                        class="donut__arc"
                        cx="70"
                        cy="70"
                        [attr.r]="donutRadius"
                        [attr.stroke]="arc.color"
                        [attr.stroke-dasharray]="arc.dash"
                        [attr.stroke-dashoffset]="arc.offset"
                      >
                        <title>{{ arc.label }}: {{ arc.count }}</title>
                      </circle>
                    }
                  }
                </svg>
                <div class="donut__center">
                  <span class="donut__value">{{ companyTotal() }}</span>
                  <span class="donut__label">companies</span>
                </div>
              </div>

              <ul class="status-legend">
                @for (arc of companyArcs(); track arc.key) {
                  <li class="status-legend__item">
                    <span class="status-legend__dot" [style.background]="arc.color"></span>
                    <span class="status-legend__label">{{ arc.label }}</span>
                    <span class="status-legend__count">{{ arc.count }}</span>
                    <span class="status-legend__pct">{{ arc.pct | number: '1.0-0' }}%</span>
                  </li>
                }
              </ul>
            }
          </div>

          <div class="split-panel__main">
            @if (isLoadingCompanies()) {
              <div class="panel__body">
                <p class="hint">Loading...</p>
              </div>
            } @else if (companies().length === 0) {
              <div class="panel__body">
                <div class="empty-hint">
                  <span class="material-icons">domain</span>
                  <p>{{ companyEmptyMessage }}</p>
                </div>
              </div>
            } @else {
              <div class="activity-list">
                @for (company of companies(); track company.id) {
                  <a [routerLink]="['/admin/companies', company.id]" class="activity-item">
                    <span class="activity-item__icon material-icons">domain</span>
                    <div class="activity-item__text">
                      <span class="activity-item__title">{{ company.name }}</span>
                      <span class="activity-item__subtitle">{{ company.countryCode }} · {{ company.registrationNo }}</span>
                    </div>
                    <app-status-badge [status]="company.status" />
                    <span class="activity-item__time">{{ company.createdAt | date: 'MMM d' }}</span>
                  </a>
                }
              </div>
            }
          </div>
        </div>
      </section>
    }

    @if (canManagePlatformUsers) {
      <section class="panel">
        <div class="panel__header panel__header--split">
          <h2 class="panel__title">Platform users</h2>
          <a routerLink="/admin/users" class="panel__link">View all</a>
        </div>

        <div class="users-panel">
          <div class="users-panel__summary">
            <span class="users-panel__caption">Accounts by role</span>
            @if (isLoadingRoles()) {
              <p class="hint">Loading...</p>
            } @else {
              <div class="scope-split">
                <div class="scope-split__item">
                  <span class="scope-split__count">{{ platformScopedTotal() }}</span>
                  <span class="scope-split__label">Platform-scoped</span>
                </div>
                <div class="scope-split__item">
                  <span class="scope-split__count">{{ companyScopedTotal() }}</span>
                  <span class="scope-split__label">Company-scoped</span>
                </div>
              </div>

              <ul class="role-bars">
                @for (row of roleSummary(); track row.role) {
                  <li class="role-bars__item">
                    <span class="role-bars__label">{{ row.label }}</span>
                    <span class="role-bars__track">
                      <span class="role-bars__fill" [style.width.%]="row.widthPct"></span>
                    </span>
                    <span class="role-bars__count">{{ row.count }}</span>
                  </li>
                }
              </ul>
            }
          </div>

          <div class="users-panel__list">
            <span class="users-panel__caption">Latest accounts</span>
            @if (isLoadingUsers()) {
              <p class="hint">Loading...</p>
            } @else if (recentUsers().length === 0) {
              <div class="empty-hint">
                <span class="material-icons">group</span>
                <p>No platform users yet.</p>
              </div>
            } @else {
              <div class="activity-list activity-list--flush">
                @for (user of recentUsers(); track user.id) {
                  <div class="activity-item activity-item--static activity-item--flush">
                    <span class="activity-item__icon material-icons">person</span>
                    <div class="activity-item__text">
                      <span class="activity-item__title">{{ user.fullName }}</span>
                      <span class="activity-item__subtitle">{{ user.email }} · {{ user.role }}</span>
                    </div>
                    <app-status-badge [status]="user.enabled ? 'ACTIVE' : 'SUSPENDED'" [label]="user.enabled ? 'Enabled' : 'Disabled'" />
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </section>
    }

    @if (recentActivityTitle()) {
      <section class="panel">
        <div class="panel__header panel__header--split">
          <h2 class="panel__title">{{ recentActivityTitle() }}</h2>
          <a [routerLink]="recentActivityLink" class="panel__link">View all</a>
        </div>
        @if (isLoadingActivity()) {
          <div class="panel__body">
            <p class="hint">Loading...</p>
          </div>
        } @else if (recentActivity().length === 0) {
          <div class="panel__body">
            <div class="empty-hint">
              <span class="material-icons">history</span>
              <p>Nothing to show here yet.</p>
            </div>
          </div>
        } @else {
          <div class="activity-list">
            @for (item of recentActivity(); track item.title + item.time) {
              <a [routerLink]="item.link" class="activity-item">
                <span class="activity-item__icon material-icons">{{ item.icon }}</span>
                <div class="activity-item__text">
                  <span class="activity-item__title">{{ item.title }}</span>
                  <span class="activity-item__subtitle">{{ item.subtitle }}</span>
                </div>
                <app-status-badge [status]="item.badgeStatus" />
                <span class="activity-item__time">{{ item.time | date: 'MMM d, h:mm a' }}</span>
              </a>
            }
          </div>
        }
      </section>
    }
  </div>
</div>
